import React, { useCallback, useEffect, useRef, useState } from "react";
const LocationPicker = ({
  setAddress,
  isSubmitted,
  customIconStyle,
  canShowFullForm = true,
  showLabel = false,
  data = {
    latitude: null,
    longitude: null,
    postal_address1: "",
    postal_address2: "",
    suburb_town: "",
    state: "",
    state_code: "",
    country: "",
    country_code: "",
    postcode: "",
  },
}) => {
  const [state, setState] = useState(data);

  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  // Typing by hand does not count as picking an address: drop everything that
  // only the Places lookup can fill in, so callers can tell the two apart.
  const handleChange = useCallback((event) => {
    const value = event.target.value;
    const nextState = {
      ...state,
      postal_address1: value,
      postal_address2: "",
      latitude: null,
      longitude: null,
      suburb_town: "",
      state: "",
      state_code: "",
      country: "",
      country_code: "",
      postcode: "",
    };
    setState(nextState);
    setAddress(nextState);
  }, [state, setAddress]);

  const handlePlaceSelect = useCallback(() => {
    let place = autocompleteRef.current?.getPlace();
    if (!place) return;

    // Pressing Enter without picking a suggestion gives back a stub that only
    // carries the typed text, keep what the user typed instead of wiping it.
    if (!place?.geometry && !place?.address_components) return;

    const address = {
      latitude: place?.geometry?.location?.lat(),
      longitude: place?.geometry?.location?.lng(),
    };

    address.postal_address1 = place?.formatted_address;

    let homeAddress = "";

    if (!place?.address_components) {
      setState({ ...address });
      setAddress({ ...address });
      return;
    }
    for (const component of place?.address_components) {
      if (!component?.types) continue;

      for (const componentType of component.types) {
        if (!componentType) continue;
        switch (componentType) {
          case "street_number": {
            homeAddress = `${component.long_name}`;
            break;
          }
          case "route": {
            if (homeAddress) homeAddress += " ";
            homeAddress += component.long_name;
            break;
          }
          case "locality": {
            address.suburb_town = component.long_name;
            break;
          }
          case "administrative_area_level_1": {
            address.state = component.long_name;
            address.state_code = component.short_name;
            break;
          }
          case "country": {
            address.country = component.long_name;
            address.country_code = component.short_name;
            break;
          }
          case "postal_code": {
            address.postcode = `${component.long_name}`;
            break;
          }
          default:
            console.log("Unknown component type:", componentType);
            break;
        }
      }
    }

    address.postal_address2 = homeAddress;

    setState({ ...address });
    setAddress({ ...address });
  }, [setAddress]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();

      // Autocomplete may not have committed selection yet; defer read.
      setTimeout(() => {
        handlePlaceSelect();
      }, 100);
    },
    [handlePlaceSelect]
  );

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let autocomplete = null;
    let dropdown = null;

    // The Maps script is loaded with a `callback`, so the Places library can
    // still be missing when this mounts. Reading window.google once at module
    // load made the picker fail whenever it lost that race.
    const init = () => {
      if (cancelled || !inputRef.current) return;

      const places = window.google?.maps?.places;
      if (!places?.Autocomplete) {
        timer = setTimeout(init, 200);
        return;
      }

      autocomplete = new places.Autocomplete(inputRef.current, {
        fields: ["address_components", "geometry", "formatted_address"],
        types: ["address"],
      });
      autocompleteRef.current = autocomplete;
      autocomplete.addListener("place_changed", handlePlaceSelect);

      // Google appends the dropdown to <body> and never takes it away, so keep
      // hold of ours to clean it up on unmount.
      const dropdowns = document.querySelectorAll(".pac-container");
      dropdown = dropdowns[dropdowns.length - 1];
    };

    init();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (autocomplete) {
        window.google?.maps?.event?.clearInstanceListeners(autocomplete);
      }
      autocompleteRef.current = null;
      dropdown?.remove();
    };
  }, [handlePlaceSelect]);

  return (
    <div className="pb-3">
      <div className="form-group icon-input mb-3">
        {showLabel ? (
          <label className="mont-font fw-600 font-xsss">Address</label>
        ) : null}
        <i className="font fa fa-map-marker text-grey-500 pe-0" style={customIconStyle} />
        <input
          id="autocomplete"
          ref={inputRef}
          value={state?.postal_address1 || ""}
          required
          type="text"
          className={`style2-input ps-5 form-control text-grey-900 font-xsss fw-600
          ${isSubmitted && !state?.postal_address1 ? 'red-input' : ''}`}
          placeholder="Find your address here ..."
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </div>

      {state?.country ? (
        <>
          {" "}
          {/* <div className="form-group mb-3">
            <label className="mont-font fw-600 font-xsss">
              Postal address line 1
            </label>
            <input
              name="postal_address1"
              value={state?.postal_address1}
              onChange={handleChange}
              type="text"
              className="style2-input ps-3 form-control text-grey-900 font-xsss fw-600"
              placeholder="Find your address here ..."
            />
          </div> */}
          {/* <div className="form-group">
            <label className="mont-font fw-600 font-xsss">Postal address</label>
            <input
              name="postal_address2"
              onChange={handleChange}
              value={state?.postal_address2}
              type="text"
              className="style2-input ps-3 form-control text-grey-900 font-xsss fw-600"
              placeholder="Find your address here ..."
            />
          </div> */}
          {canShowFullForm ? (
            <>
              <div className="row mt-3">
                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="mont-font fw-600 font-xsss">Town</label>
                    <input
                      onKeyDown={(e) => {
                        e.key === "Enter" && e.preventDefault();
                      }}
                      value={state?.suburb_town}
                      type="text"
                      className="style2-input ps-3 form-control text-grey-900 font-xsss fw-600"
                      placeholder="Find your address here ..."
                      readOnly
                    />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="mont-font fw-600 font-xsss">State</label>
                    <input
                      value={`${state?.state}${state?.state_code && ` (${state?.state_code})`
                        }`}
                      type="text"
                      className="style2-input ps-3 form-control text-grey-900 font-xsss fw-600"
                      placeholder="Find your address here ..."
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="mont-font fw-600 font-xsss">
                      Country
                    </label>
                    <input
                      value={`${state?.country?.description ?? state?.country}${state?.country_code && ` (${state?.country_code})`
                        }`}
                      type="text"
                      className="style2-input ps-3 form-control text-grey-900 font-xsss fw-600"
                      placeholder="Find your address here ..."
                      readOnly
                    />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="mont-font fw-600 font-xsss">
                      Post Code
                    </label>
                    <input
                      value={state?.postcode}
                      type="text"
                      className="style2-input ps-3 form-control text-grey-900 font-xsss fw-600"
                      placeholder="Find your address here ..."
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default LocationPicker;
